import { randomUUID } from "node:crypto";
import {
	type AgentToolProvider,
	type AgentToolRunContext,
	defineAgentToolProvider,
} from "@aiverse/core";
import { type JSONValue, type ToolSet, tool } from "ai";
import type { Browser, BrowserContext, ElementHandle, Page } from "playwright";
import { z } from "zod";
import { BrowserNetworkPolicy, BrowserPolicyError } from "./network-policy";
import {
	BROWSER_TOOL_NAMES,
	type BrowserAction,
	type BrowserReceipt,
	type BrowserRunDisposeReason,
	type PlaywrightToolProviderOptions,
	type RemoteBrowserSession,
} from "./types";

const DEFAULT_NAVIGATION_TIMEOUT_MS = 30_000;
const DEFAULT_ACTION_TIMEOUT_MS = 5_000;
const DEFAULT_MAX_SNAPSHOT_BYTES = 32 * 1024;
const DEFAULT_MAX_SCREENSHOT_BYTES = 1024 * 1024;
const DEFAULT_MAX_REFS = 200;
const MAX_SCREENSHOT_PIXELS = 16_000_000;

const ACTION_LABELS: Record<BrowserAction, string> = {
	open: "Open browser",
	navigate: "Navigate browser",
	back: "Go back",
	snapshot: "Inspect page",
	"follow-link": "Follow link",
	click: "Click control",
	type: "Type into page",
	submit: "Submit form",
	screenshot: "Capture screenshot",
	close: "Close browser",
};

type ResolvedLimits = {
	navigationTimeoutMs: number;
	actionTimeoutMs: number;
	maxSnapshotBytes: number;
	maxScreenshotBytes: number;
	maxRefs: number;
};

type ElementInfo = {
	tag: string;
	role: string;
	type: string;
	name: string;
	href?: string;
	download: boolean;
	disabled: boolean;
	editable: boolean;
	sensitive: boolean;
	sensitiveReason?: string;
};

type ElementRef = ElementInfo & {
	handle: ElementHandle<Element>;
};

type PrivateModelOutput =
	| { kind: "snapshot"; text: string }
	| { kind: "screenshot"; data: string; receiptText: string };

type ActiveSession = {
	browser: Browser;
	context: BrowserContext;
	page: Page;
	ownedBrowser: boolean;
	destroyed?: boolean;
	remote?: RemoteBrowserSession;
	liveViewId?: string;
	lastBlocked?: BrowserPolicyError;
};

class BrowserReferenceError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "BrowserReferenceError";
	}
}

function positiveLimit(
	value: number | undefined,
	fallback: number,
	name: string,
): number {
	if (value === undefined) return fallback;
	if (!Number.isSafeInteger(value) || value <= 0) {
		throw new TypeError(`${name} must be a positive safe integer`);
	}
	return value;
}

function resolveLimits(options: PlaywrightToolProviderOptions): ResolvedLimits {
	return {
		navigationTimeoutMs: positiveLimit(
			options.limits?.navigationTimeoutMs,
			DEFAULT_NAVIGATION_TIMEOUT_MS,
			"limits.navigationTimeoutMs",
		),
		actionTimeoutMs: positiveLimit(
			options.limits?.actionTimeoutMs,
			DEFAULT_ACTION_TIMEOUT_MS,
			"limits.actionTimeoutMs",
		),
		maxSnapshotBytes: positiveLimit(
			options.limits?.maxSnapshotBytes,
			DEFAULT_MAX_SNAPSHOT_BYTES,
			"limits.maxSnapshotBytes",
		),
		maxScreenshotBytes: positiveLimit(
			options.limits?.maxScreenshotBytes,
			DEFAULT_MAX_SCREENSHOT_BYTES,
			"limits.maxScreenshotBytes",
		),
		maxRefs: positiveLimit(
			options.limits?.maxRefs,
			DEFAULT_MAX_REFS,
			"limits.maxRefs",
		),
	};
}

function clampText(value: string, max = 500): string {
	const oneLine = value.replace(/\s+/g, " ").trim();
	return oneLine.length <= max ? oneLine : `${oneLine.slice(0, max - 1)}…`;
}

function truncateUtf8(
	value: string,
	maxBytes: number,
): { value: string; truncated: boolean } {
	const bytes = Buffer.from(value, "utf8");
	if (bytes.byteLength <= maxBytes) return { value, truncated: false };
	const suffix = Buffer.from("\n… snapshot truncated …", "utf8");
	const bodyLimit = Math.max(0, maxBytes - suffix.byteLength);
	let body = bytes.subarray(0, bodyLimit).toString("utf8");
	// Buffer slicing may leave a replacement character at a UTF-8 boundary.
	body = body.replace(/\uFFFD$/, "");
	return { value: `${body}${suffix.toString("utf8")}`, truncated: true };
}

function errorMessage(error: unknown): string {
	if (error instanceof Error) return clampText(error.message);
	return clampText(String(error));
}

function disposeReasonFromSignal(signal: AbortSignal): BrowserRunDisposeReason {
	const reason = signal.reason;
	return reason === "complete" ||
		reason === "abort" ||
		reason === "error" ||
		reason === "close"
		? reason
		: "abort";
}

function receiptText(receipt: BrowserReceipt): string {
	return [
		`${receipt.label}: ${receipt.status}`,
		receipt.title ? `Title: ${receipt.title}` : null,
		receipt.url ? `URL: ${receipt.url}` : null,
		receipt.message ?? null,
	]
		.filter((part): part is string => Boolean(part))
		.join("\n");
}

function metadata(action: BrowserAction) {
	return { aiverseKind: "browser", action } as const;
}

function elementInfoInPage(element: Element): ElementInfo {
	const html = element as HTMLElement;
	const tag = element.tagName.toLowerCase();
	const type =
		tag === "input"
			? ((element as HTMLInputElement).type || "text").toLowerCase()
			: "";
	const explicitRole = element.getAttribute("role")?.toLowerCase() ?? "";
	let role = explicitRole;
	if (!role) {
		if (tag === "a" && (element as HTMLAnchorElement).href) role = "link";
		else if (tag === "button") role = "button";
		else if (tag === "textarea") role = "textbox";
		else if (tag === "select") role = "combobox";
		else if (tag === "summary") role = "button";
		else if (tag === "input") {
			if (["button", "submit", "reset", "image"].includes(type))
				role = "button";
			else if (type === "checkbox") role = "checkbox";
			else if (type === "radio") role = "radio";
			else role = "textbox";
		} else role = "control";
	}
	const ariaLabel = element.getAttribute("aria-label") ?? "";
	const labelledBy = (element.getAttribute("aria-labelledby") ?? "")
		.split(/\s+/)
		.filter(Boolean)
		.map((id) => document.getElementById(id)?.textContent ?? "")
		.join(" ");
	const labelText = Array.from(
		(element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement)
			.labels ?? [],
	)
		.map((label) => label.textContent ?? "")
		.join(" ");
	const placeholder = element.getAttribute("placeholder") ?? "";
	const title = element.getAttribute("title") ?? "";
	const inputValue =
		tag === "input" && ["button", "submit", "reset"].includes(type)
			? (element as HTMLInputElement).value
			: "";
	const name =
		ariaLabel ||
		labelledBy ||
		labelText ||
		placeholder ||
		title ||
		inputValue ||
		html.innerText ||
		element.textContent ||
		element.getAttribute("name") ||
		element.getAttribute("id") ||
		role;
	const autocomplete =
		element.getAttribute("autocomplete")?.toLowerCase() ?? "";
	const paymentHaystack = [
		ariaLabel,
		labelledBy,
		labelText,
		placeholder,
		title,
		element.getAttribute("name") ?? "",
		element.getAttribute("id") ?? "",
		autocomplete,
	]
		.join(" ")
		.toLowerCase();
	const payment =
		/\b(?:credit|debit|card|cardholder|cvv|cvc|iban|routing|bank account|expiry|expiration)\b/.test(
			paymentHaystack,
		) || autocomplete.startsWith("cc-");
	const credential =
		/\b(?:pass(?:word|code|phrase)?|security code|one[- ]time code|otp)\b/.test(
			paymentHaystack,
		) ||
		["current-password", "new-password", "one-time-code"].includes(
			autocomplete,
		);
	const sensitiveReason =
		type === "password" || credential
			? "password fields are excluded"
			: type === "file"
				? "file fields and uploads are excluded"
				: payment
					? "payment fields are excluded"
					: undefined;
	const editableTypes = new Set([
		"text",
		"search",
		"email",
		"url",
		"tel",
		"number",
		"date",
		"datetime-local",
		"month",
		"time",
		"week",
	]);
	return {
		tag,
		role,
		type,
		name,
		href:
			tag === "a" && (element as HTMLAnchorElement).href
				? (element as HTMLAnchorElement).href
				: undefined,
		download:
			tag === "a" &&
			(element as HTMLAnchorElement).hasAttribute("download"),
		disabled:
			"disabled" in html && Boolean((html as HTMLButtonElement).disabled),
		editable:
			tag === "textarea" ||
			html.isContentEditable ||
			(tag === "input" && editableTypes.has(type)) ||
			explicitRole === "textbox",
		sensitive: sensitiveReason !== undefined,
		sensitiveReason,
	};
}

class BrowserRun {
	private readonly limits: ResolvedLimits;
	private readonly policy: BrowserNetworkPolicy;
	private readonly refs = new Map<string, ElementRef>();
	private readonly privateOutputs = new Map<string, PrivateModelOutput>();
	private session?: ActiveSession;
	private sessionPromise?: Promise<ActiveSession>;
	private serial: Promise<void> = Promise.resolve();
	private disposed = false;
	private disposalReason?: BrowserRunDisposeReason;

	constructor(
		private readonly options: PlaywrightToolProviderOptions,
		private readonly runContext: AgentToolRunContext,
	) {
		this.limits = resolveLimits(options);
		this.policy = new BrowserNetworkPolicy(options.allowedOrigins);
	}

	tools(): ToolSet {
		return {
			"browser-open": tool({
				title: ACTION_LABELS.open,
				description:
					"Open an isolated browser at a public HTTP(S) URL. This starts the browser lazily. Use browser-snapshot after opening to inspect the page.",
				metadata: metadata("open"),
				inputSchema: z.object({
					url: z
						.string()
						.describe("Absolute public HTTP(S) URL to open."),
				}),
				execute: ({ url }) =>
					this.perform("open", async () => {
						const checked = await this.policy.check(url);
						const session = await this.ensureSession();
						// Recheck during navigation and route interception as defense against
						// DNS rebinding between validation and Chromium's request.
						await this.navigate(session, checked.href);
						return this.pageReceipt("open", session, "Opened page");
					}),
			}),
			"browser-navigate": tool({
				title: ACTION_LABELS.navigate,
				description:
					"Navigate the active isolated browser to a public HTTP(S) URL. Relative URLs resolve against the current page.",
				metadata: metadata("navigate"),
				inputSchema: z.object({
					url: z
						.string()
						.describe("Absolute or current-page-relative URL."),
				}),
				execute: ({ url }) =>
					this.perform("navigate", async () => {
						const session = this.requireSession();
						await this.navigate(session, url, session.page.url());
						return this.pageReceipt(
							"navigate",
							session,
							"Navigation complete",
						);
					}),
			}),
			"browser-back": tool({
				title: ACTION_LABELS.back,
				description: "Go back once in the active browser's history.",
				metadata: metadata("back"),
				inputSchema: z.object({}),
				execute: () =>
					this.perform("back", async () => {
						const session = this.requireSession();
						session.lastBlocked = undefined;
						try {
							await session.page.goBack({
								waitUntil: "domcontentloaded",
								timeout: this.limits.navigationTimeoutMs,
							});
						} catch (error) {
							throw session.lastBlocked ?? error;
						}
						await this.checkCurrentPage(session);
						await this.clearRefs();
						return this.pageReceipt("back", session, "Went back");
					}),
			}),
			"browser-snapshot": tool({
				title: ACTION_LABELS.snapshot,
				description:
					"Inspect the current page as a capped accessibility snapshot and create opaque element references for later browser tools. Call it again after navigation or a stale-reference error.",
				metadata: metadata("snapshot"),
				inputSchema: z.object({}),
				execute: (_input, { toolCallId }) =>
					this.perform("snapshot", async () => {
						const session = this.requireSession();
						const snapshot = await this.snapshot(session);
						this.privateOutputs.set(toolCallId, {
							kind: "snapshot",
							text: snapshot.text,
						});
						return this.pageReceipt(
							"snapshot",
							session,
							"Page inspected",
							{
								refCount: snapshot.refCount,
								truncated: snapshot.truncated,
							},
						);
					}),
				toModelOutput: ({ toolCallId, output }) =>
					this.toModelOutput(toolCallId, output),
			}),
			"browser-follow-link": tool({
				title: ACTION_LABELS["follow-link"],
				description:
					"Follow a verified link from the latest accessibility snapshot by opaque ref. This navigates directly to the link's checked href and does not execute a synthetic click.",
				metadata: metadata("follow-link"),
				inputSchema: z.object({
					ref: z
						.string()
						.describe("Opaque link ref from browser-snapshot."),
				}),
				execute: ({ ref }) =>
					this.perform("follow-link", async () => {
						const session = this.requireSession();
						const entry = await this.requireRef(ref);
						const current = await this.revalidateTarget(
							entry,
							entry,
						);
						if (current.role !== "link" || !current.href) {
							throw new BrowserReferenceError(
								`Reference ${JSON.stringify(ref)} is not a verified link. Run browser-snapshot again.`,
							);
						}
						if (current.download) {
							throw new BrowserReferenceError(
								"Downloads are excluded from browser v1.",
							);
						}
						await this.navigate(session, current.href);
						return this.pageReceipt(
							"follow-link",
							session,
							"Followed link",
						);
					}),
			}),
			"browser-click": tool({
				title: ACTION_LABELS.click,
				description:
					"Click a non-sensitive control by opaque snapshot ref. Always requires explicit human approval, including in YOLO mode. Prefer browser-follow-link for links.",
				metadata: metadata("click"),
				inputSchema: z.object({
					ref: z
						.string()
						.describe("Opaque element ref from browser-snapshot."),
				}),
				execute: ({ ref }) =>
					this.perform("click", async () => {
						const session = this.requireSession();
						const entry = await this.requireRef(ref);
						const current =
							await entry.handle.evaluate(elementInfoInPage);
						this.assertAllowedElement(current);
						const rejected = await this.requireApproval(
							"click",
							"side-effect",
							`Click ${JSON.stringify(clampText(current.name, 120))} on ${clampText(session.page.url(), 180)}?`,
							{
								ref,
								role: current.role,
								field: clampText(current.name, 200),
							},
						);
						if (rejected) return rejected;
						await this.revalidateTarget(entry, current);
						session.lastBlocked = undefined;
						try {
							await entry.handle.click({
								timeout: this.limits.actionTimeoutMs,
							});
						} catch (error) {
							throw session.lastBlocked ?? error;
						}
						await this.checkCurrentPage(session);
						await this.clearRefs();
						return this.pageReceipt(
							"click",
							session,
							"Clicked approved control",
						);
					}),
			}),
			"browser-type": tool({
				title: ACTION_LABELS.type,
				description:
					"Replace the value of a non-sensitive editable field by opaque ref. Password, payment, and file fields are forbidden. Always requires explicit human approval, including in YOLO mode.",
				metadata: metadata("type"),
				inputSchema: z.object({
					ref: z
						.string()
						.describe(
							"Opaque editable-field ref from browser-snapshot.",
						),
					text: z
						.string()
						.max(10_000)
						.describe("Exact text to enter."),
				}),
				execute: ({ ref, text }) =>
					this.perform("type", async () => {
						const session = this.requireSession();
						const entry = await this.requireRef(ref);
						const current =
							await entry.handle.evaluate(elementInfoInPage);
						this.assertAllowedElement(current);
						if (!current.editable) {
							throw new BrowserReferenceError(
								`Reference ${JSON.stringify(ref)} is not an editable text field.`,
							);
						}
						const proposed = clampText(text, 200);
						const rejected = await this.requireApproval(
							"type",
							"typing",
							`Type ${JSON.stringify(proposed)} into ${JSON.stringify(clampText(current.name, 120))}?`,
							{
								ref,
								field: clampText(current.name, 200),
								text: proposed,
								textTruncated: proposed !== text,
							},
						);
						if (rejected) return rejected;
						await this.revalidateTarget(entry, current);
						await entry.handle.fill(text, {
							timeout: this.limits.actionTimeoutMs,
						});
						await this.checkCurrentPage(session);
						return this.pageReceipt(
							"type",
							session,
							`Typed into ${clampText(current.name, 120)}`,
						);
					}),
			}),
			"browser-submit": tool({
				title: ACTION_LABELS.submit,
				description:
					"Submit using a non-sensitive button or field from the latest snapshot. Always requires explicit human approval, including in YOLO mode.",
				metadata: metadata("submit"),
				inputSchema: z.object({
					ref: z
						.string()
						.describe("Opaque submit-button or form-field ref."),
				}),
				execute: ({ ref }) =>
					this.perform("submit", async () => {
						const session = this.requireSession();
						const entry = await this.requireRef(ref);
						const current =
							await entry.handle.evaluate(elementInfoInPage);
						this.assertAllowedElement(current);
						const rejected = await this.requireApproval(
							"submit",
							"side-effect",
							`Submit using ${JSON.stringify(clampText(current.name, 120))} on ${clampText(session.page.url(), 180)}?`,
							{
								ref,
								role: current.role,
								field: clampText(current.name, 200),
							},
						);
						if (rejected) return rejected;
						await this.revalidateTarget(entry, current);
						session.lastBlocked = undefined;
						try {
							if (
								current.role === "button" ||
								current.type === "submit"
							) {
								await entry.handle.click({
									timeout: this.limits.actionTimeoutMs,
								});
							} else if (current.editable) {
								await entry.handle.press("Enter", {
									timeout: this.limits.actionTimeoutMs,
								});
							} else {
								throw new BrowserReferenceError(
									`Reference ${JSON.stringify(ref)} cannot submit a form.`,
								);
							}
						} catch (error) {
							throw session.lastBlocked ?? error;
						}
						await this.checkCurrentPage(session);
						await this.clearRefs();
						return this.pageReceipt(
							"submit",
							session,
							"Submitted with approval",
						);
					}),
			}),
			"browser-screenshot": tool({
				title: ACTION_LABELS.screenshot,
				description:
					"Capture a PNG of the active page for visual inspection. The image is capped and sent only to the model, never persisted in the UI receipt.",
				metadata: metadata("screenshot"),
				inputSchema: z.object({
					fullPage: z
						.boolean()
						.optional()
						.describe(
							"Capture the full document instead of the viewport. Default false.",
						),
				}),
				execute: ({ fullPage }, { toolCallId }) =>
					this.perform("screenshot", async () => {
						const session = this.requireSession();
						const bounds = fullPage
							? await session.page.locator("html").boundingBox({
									timeout: this.limits.actionTimeoutMs,
								})
							: session.page.viewportSize();
						const scale =
							this.options.contextOptions?.deviceScaleFactor ?? 1;
						if (
							bounds &&
							bounds.width * bounds.height * scale * scale >
								MAX_SCREENSHOT_PIXELS
						) {
							throw new Error(
								"Page is too large to capture safely. Use a smaller viewport screenshot.",
							);
						}
						const png = await session.page.screenshot({
							type: "png",
							fullPage: fullPage ?? false,
							animations: "disabled",
							timeout: this.limits.actionTimeoutMs,
						});
						if (png.byteLength > this.limits.maxScreenshotBytes) {
							throw new Error(
								`Screenshot is ${png.byteLength} bytes, exceeding the ${this.limits.maxScreenshotBytes}-byte limit. Try a viewport screenshot.`,
							);
						}
						const output = await this.pageReceipt(
							"screenshot",
							session,
							"Screenshot captured for model inspection",
							{ bytes: png.byteLength },
						);
						this.privateOutputs.set(toolCallId, {
							kind: "screenshot",
							data: png.toString("base64"),
							receiptText: receiptText(output),
						});
						return output;
					}),
				toModelOutput: ({ toolCallId, output }) =>
					this.toModelOutput(toolCallId, output),
			}),
			"browser-close": tool({
				title: ACTION_LABELS.close,
				description:
					"Close the active isolated browser session. A later browser-open can start a fresh session in the same root run.",
				metadata: metadata("close"),
				inputSchema: z.object({}),
				execute: () =>
					this.perform("close", async () => {
						await this.closeSession("complete");
						return this.makeReceipt(
							"close",
							"closed",
							"Browser session closed",
						);
					}),
			}),
		};
	}

	async dispose(reason: BrowserRunDisposeReason): Promise<void> {
		if (this.disposed) return;
		this.disposed = true;
		this.disposalReason = reason;
		await this.closeSession(reason);
		this.privateOutputs.clear();
	}

	/** Interrupt an in-flight Playwright call; lifecycle disposal still owns release. */
	interrupt(): void {
		void this.session?.context.close().catch(() => {});
	}

	private perform(
		action: BrowserAction,
		operation: () => Promise<BrowserReceipt>,
	): Promise<BrowserReceipt> {
		const result = this.serial.then(async () => {
			if (this.disposed || this.runContext.signal.aborted) {
				return this.makeReceipt(
					action,
					"error",
					"Browser run was stopped",
				);
			}
			try {
				return await operation();
			} catch (error) {
				const status =
					error instanceof BrowserPolicyError ? "blocked" : "error";
				return this.makeReceipt(
					action,
					status,
					errorMessage(error),
					this.session,
				);
			}
		});
		this.serial = result.then(
			() => undefined,
			() => undefined,
		);
		return result;
	}

	private async createSession(): Promise<ActiveSession> {
		let browser: Browser;
		let ownedBrowser = false;
		let remote: RemoteBrowserSession | undefined;
		const connection = this.options.connection ?? {
			mode: "local" as const,
		};

		if (connection.mode === "remote") {
			try {
				remote = await connection.connect(this.runContext);
			} catch {
				// Host connection errors can contain CDP or signed service URLs. Never
				// put those details in a model/UI receipt.
				throw new Error("Remote browser connection failed");
			}
			browser = remote.browser;
			if (!browser?.isConnected()) {
				await Promise.resolve()
					.then(() => remote?.release?.("error"))
					.catch(() => {});
				throw new Error(
					"Remote browser connector returned a disconnected browser",
				);
			}
		} else {
			let playwright: typeof import("playwright");
			try {
				playwright = await import("playwright");
			} catch {
				throw new Error(
					'Playwright is not installed. Install the "playwright" peer and run "playwright install chromium".',
				);
			}
			browser = await playwright.chromium.launch({
				...connection.launchOptions,
				args: [
					...(connection.launchOptions?.args ?? []),
					"--force-webrtc-ip-handling-policy=disable_non_proxied_udp",
					"--webrtc-ip-handling-policy=disable_non_proxied_udp",
				],
				headless: connection.headless ?? false,
			});
			ownedBrowser = true;
		}

		let context: BrowserContext;
		try {
			context = await browser.newContext({
				...this.options.contextOptions,
				acceptDownloads: false,
				recordHar: undefined,
				recordVideo: undefined,
				serviceWorkers: "block",
				storageState: undefined,
			});
		} catch (error) {
			if (ownedBrowser) await browser.close().catch(() => {});
			else
				await Promise.resolve()
					.then(() => remote?.release?.("error"))
					.catch(() => {});
			throw ownedBrowser
				? error
				: new Error("Remote browser context creation failed");
		}
		context.setDefaultTimeout(this.limits.actionTimeoutMs);
		context.setDefaultNavigationTimeout(this.limits.navigationTimeoutMs);

		const session: ActiveSession = {
			browser,
			context,
			page: undefined as unknown as Page,
			ownedBrowser,
			remote,
		};

		try {
			// Prevent page scripts from opening non-HTTP peer/WebTransport channels.
			// This is defense in depth for local development; production remote
			// browsers should additionally enforce an infrastructure egress policy.
			await context.addInitScript(() => {
				for (const name of [
					"RTCPeerConnection",
					"webkitRTCPeerConnection",
					"WebTransport",
				]) {
					try {
						Object.defineProperty(globalThis, name, {
							value: undefined,
							configurable: false,
							writable: false,
						});
					} catch {
						// A runtime may not expose the API, which is already the safe state.
					}
				}
			});
			await context.route("**/*", async (route) => {
				try {
					await this.policy.check(route.request().url());
					await route.continue();
				} catch (error) {
					if (error instanceof BrowserPolicyError)
						session.lastBlocked = error;
					await route.abort("blockedbyclient").catch(() => {});
				}
			});
			// The v1 network surface is deliberately HTTP(S)-only. Playwright's normal
			// route interception does not cover WebSockets, so close them before they
			// can connect (including public WebSockets and private-network probes).
			await context.routeWebSocket(/.*/, async (webSocket) => {
				await webSocket.close({
					code: 1008,
					reason: "WebSockets are disabled in the isolated browser",
				});
			});

			let primaryPage: Page | undefined;
			context.on("page", (page) => {
				if (primaryPage && page !== primaryPage) {
					// Tabs/popups are deliberately not part of the v1 tool surface.
					void page.close().catch(() => {});
				}
			});
			const page = await context.newPage();
			primaryPage = page;
			session.page = page;
			page.on("dialog", (dialog) => {
				// Dialogs cannot receive a separate approval and otherwise deadlock tools.
				void dialog.dismiss().catch(() => {});
			});
			page.on("download", (download) => {
				void download.cancel().catch(() => {});
			});
			page.on("filechooser", (fileChooser) => {
				// A page may open a hidden file input from an otherwise ordinary button.
				// Clear it immediately; there is intentionally no upload tool in v1.
				void fileChooser.setFiles([]).catch(() => {});
			});
			page.on("framenavigated", (frame) => {
				if (frame !== page.mainFrame() || frame.url() === "about:blank")
					return;
				void this.policy.check(frame.url()).catch((error) => {
					if (error instanceof BrowserPolicyError)
						session.lastBlocked = error;
					// Route interception covers HTTP redirects; this closes the gap for
					// script-driven top-level navigation to a forbidden scheme.
					void context.close().catch(() => {});
				});
			});

			if (remote?.liveView) {
				const liveView = remote.liveView;
				session.liveViewId = this.runContext.registerEphemeralLink({
					resolve: () => {
						if (session.destroyed)
							throw new Error("Browser session has ended");
						return liveView.resolve();
					},
					expiresAt: liveView.expiresAt,
					allowInsecureLocalhost: liveView.allowInsecureLocalhost,
				});
			}
			if (this.disposed || this.runContext.signal.aborted) {
				throw new Error("Browser run was stopped");
			}
			return session;
		} catch (error) {
			await this.destroySession(
				session,
				this.disposalReason ??
					(this.runContext.signal.aborted
						? disposeReasonFromSignal(this.runContext.signal)
						: "error"),
			).catch(() => {});
			throw error;
		}
	}

	private async ensureSession(): Promise<ActiveSession> {
		if (this.session) return this.session;
		if (!this.sessionPromise) {
			this.sessionPromise = this.createSession().then((session) => {
				this.session = session;
				return session;
			});
		}
		try {
			return await this.sessionPromise;
		} finally {
			this.sessionPromise = undefined;
		}
	}

	private requireSession(): ActiveSession {
		if (!this.session) {
			throw new Error("No browser is open. Call browser-open first.");
		}
		return this.session;
	}

	private async navigate(
		session: ActiveSession,
		rawUrl: string,
		base?: string,
	): Promise<void> {
		const url = await this.policy.check(rawUrl, base);
		session.lastBlocked = undefined;
		try {
			await session.page.goto(url.href, {
				waitUntil: "domcontentloaded",
				timeout: this.limits.navigationTimeoutMs,
			});
		} catch (error) {
			throw session.lastBlocked ?? error;
		}
		await this.checkCurrentPage(session);
		await this.clearRefs();
	}

	private async checkCurrentPage(session: ActiveSession): Promise<void> {
		const current = session.page.url();
		if (current === "about:blank") return;
		try {
			await this.policy.check(current);
		} catch (error) {
			// If script somehow moved outside policy, tear down the context before
			// reporting the violation so it cannot continue making requests.
			await this.closeSession("error");
			throw error;
		}
	}

	private async snapshot(
		session: ActiveSession,
	): Promise<{ text: string; refCount: number; truncated: boolean }> {
		await this.clearRefs();
		const lines: string[] = [];
		const candidates = session.page.locator(
			'a[href],button,input,textarea,select,summary,[role],[tabindex],[contenteditable="true"]',
		);
		const count = await candidates.count();
		const scanLimit = Math.min(count, this.limits.maxRefs * 5);
		for (
			let index = 0;
			index < scanLimit && this.refs.size < this.limits.maxRefs;
			index++
		) {
			const handle = await candidates.nth(index).elementHandle();
			if (!handle) continue;
			try {
				if (!(await handle.isVisible())) {
					await handle.dispose();
					continue;
				}
				const info = await handle.evaluate(elementInfoInPage);
				if (info.disabled || info.sensitive || info.download) {
					await handle.dispose();
					continue;
				}
				const ref = `br_${randomUUID()}`;
				this.refs.set(ref, { ...info, handle });
				const target = info.href
					? ` -> ${JSON.stringify(info.href)}`
					: "";
				const inputType = info.type
					? ` type=${JSON.stringify(info.type)}`
					: "";
				lines.push(
					`[${ref}] ${info.role}${inputType} ${JSON.stringify(clampText(info.name, 240))}${target}`,
				);
			} catch {
				await handle.dispose().catch(() => {});
			}
		}

		let aria: string;
		try {
			aria = await session.page.locator("body").ariaSnapshot({
				timeout: this.limits.actionTimeoutMs,
			});
		} catch {
			aria = clampText(
				await session.page.locator("body").innerText({
					timeout: this.limits.actionTimeoutMs,
				}),
			);
		}
		const title = await this.safeTitle(session.page);
		const full = [
			`URL: ${session.page.url()}`,
			title ? `Title: ${title}` : null,
			"",
			"Interactive elements (use only these opaque refs):",
			lines.length ? lines.join("\n") : "(none)",
			"",
			"Accessibility tree:",
			aria,
		]
			.filter((line): line is string => line !== null)
			.join("\n");
		const capped = truncateUtf8(full, this.limits.maxSnapshotBytes);
		return {
			text: capped.value,
			refCount: this.refs.size,
			truncated: capped.truncated,
		};
	}

	private async requireRef(ref: string): Promise<ElementRef> {
		const entry = this.refs.get(ref);
		if (!entry) {
			throw new BrowserReferenceError(
				`Unknown or stale browser reference ${JSON.stringify(ref)}. Run browser-snapshot again.`,
			);
		}
		try {
			const connected = await entry.handle.evaluate(
				(element) => element.isConnected,
			);
			if (!connected || !(await entry.handle.isVisible()))
				throw new Error("stale");
		} catch {
			this.refs.delete(ref);
			await entry.handle.dispose().catch(() => {});
			throw new BrowserReferenceError(
				`Browser reference ${JSON.stringify(ref)} is stale. Run browser-snapshot again.`,
			);
		}
		return entry;
	}

	private assertAllowedElement(info: ElementInfo): void {
		if (info.sensitive) {
			throw new BrowserReferenceError(
				`This target is blocked: ${info.sensitiveReason ?? "sensitive fields are excluded"}.`,
			);
		}
		if (info.download) {
			throw new BrowserReferenceError(
				"Downloads are excluded from browser v1.",
			);
		}
		if (info.disabled)
			throw new BrowserReferenceError("This control is disabled.");
	}

	private async revalidateTarget(
		entry: ElementRef,
		approved: ElementInfo,
	): Promise<ElementInfo> {
		const current = await entry.handle.evaluate(elementInfoInPage);
		this.assertAllowedElement(current);
		if (
			current.tag !== approved.tag ||
			current.role !== approved.role ||
			current.type !== approved.type ||
			current.name !== approved.name ||
			current.href !== approved.href ||
			current.editable !== approved.editable
		) {
			throw new BrowserReferenceError(
				"The referenced target changed. Run browser-snapshot and try again.",
			);
		}
		return current;
	}

	private async requireApproval(
		action: BrowserAction,
		kind: "typing" | "side-effect",
		summary: string,
		details: Record<string, unknown>,
	): Promise<BrowserReceipt | null> {
		if (!this.runContext.canApprove) {
			return this.makeReceipt(
				action,
				"unavailable",
				"This browser operation requires interactive human approval, which is unavailable on this surface.",
				this.session,
			);
		}
		const decision = await this.runContext.requestApproval({
			kind,
			summary: clampText(summary, 500),
			details,
		});
		if (decision === "approved") return null;
		return this.makeReceipt(
			action,
			decision === "denied" ? "denied" : "unavailable",
			decision === "denied"
				? "The user denied this browser operation."
				: "Interactive approval was unavailable; the browser operation was not performed.",
			this.session,
		);
	}

	private toModelOutput(toolCallId: string, output: unknown) {
		const privateOutput = this.privateOutputs.get(toolCallId);
		if (privateOutput?.kind === "snapshot") {
			return { type: "text" as const, value: privateOutput.text };
		}
		if (privateOutput?.kind === "screenshot") {
			return {
				type: "content" as const,
				value: [
					{ type: "text" as const, text: privateOutput.receiptText },
					{
						type: "image-data" as const,
						data: privateOutput.data,
						mediaType: "image/png",
					},
				],
			};
		}
		return {
			type: "json" as const,
			value: (output ?? null) as JSONValue,
		};
	}

	private async pageReceipt(
		action: BrowserAction,
		session: ActiveSession,
		message: string,
		extra: Partial<BrowserReceipt> = {},
	): Promise<BrowserReceipt> {
		return this.makeReceipt(action, "ok", message, session, {
			url: session.page.url(),
			title: await this.safeTitle(session.page),
			...extra,
		});
	}

	private makeReceipt(
		action: BrowserAction,
		status: BrowserReceipt["status"],
		message: string,
		session?: ActiveSession,
		extra: Partial<BrowserReceipt> = {},
	): BrowserReceipt {
		return {
			aiverseKind: "browser",
			action,
			status,
			label: ACTION_LABELS[action],
			message: clampText(message),
			...(session?.liveViewId ? { liveViewId: session.liveViewId } : {}),
			...extra,
		};
	}

	private async safeTitle(page: Page): Promise<string | undefined> {
		try {
			const title = clampText(await page.title(), 300);
			return title || undefined;
		} catch {
			return undefined;
		}
	}

	private async clearRefs(): Promise<void> {
		const refs = [...this.refs.values()];
		this.refs.clear();
		await Promise.allSettled(refs.map(({ handle }) => handle.dispose()));
	}

	private async closeSession(reason: BrowserRunDisposeReason): Promise<void> {
		await this.clearRefs();
		let session = this.session;
		this.session = undefined;
		if (!session && this.sessionPromise) {
			try {
				session = await this.sessionPromise;
			} catch {
				// Connection/launch already failed; nothing remains to close here.
			}
		}
		this.sessionPromise = undefined;
		this.session = undefined;
		if (session) await this.destroySession(session, reason);
	}

	private async destroySession(
		session: ActiveSession,
		reason: BrowserRunDisposeReason,
	): Promise<void> {
		if (session.destroyed) return;
		session.destroyed = true;
		await session.context.close().catch(() => {});
		if (session.ownedBrowser) {
			await session.browser.close().catch(() => {});
		} else {
			// A host release error must not leak a managed-service endpoint into
			// persisted tool output or prevent the root runtime from finishing.
			await Promise.resolve()
				.then(() => session.remote?.release?.(reason))
				.catch(() => {});
		}
	}
}

const INSTRUCTIONS = `
You can browse the external web in a fresh, isolated browser context.
- Start with browser-open, then call browser-snapshot to inspect page content and obtain opaque element refs.
- Use browser-follow-link for verified links. Never invent or reuse refs after navigation; take a new snapshot.
- browser-click, browser-type, and browser-submit always require real human approval. If approval is unavailable or denied, do not retry or work around it.
- Password, payment, file upload, download, persistent profile, arbitrary selector, and JavaScript execution workflows are unsupported.
- Close the browser when it is no longer useful. It is also closed automatically when the root run ends.
`.trim();

/**
 * Create the optional Playwright-backed `browser` agent capability.
 *
 * Configuration is side-effect free. Chromium is imported and launched (or a
 * remote lease requested) only when a root run first calls browser-open.
 */
export function createPlaywrightToolProvider(
	options: PlaywrightToolProviderOptions = {},
): AgentToolProvider {
	// Validate configuration eagerly without touching Playwright or the network.
	resolveLimits(options);
	new BrowserNetworkPolicy(options.allowedOrigins);
	const active = new Set<
		(reason: BrowserRunDisposeReason) => Promise<void>
	>();

	return defineAgentToolProvider({
		id: options.id ?? "browser",
		description:
			"Open and inspect public web pages in an isolated Playwright browser, with human approval for interactions.",
		toolNames: BROWSER_TOOL_NAMES,
		supports: ({ surface }) => surface === "chat",
		instructions: INSTRUCTIONS,
		beginRun: (context) => {
			const browserRun = new BrowserRun(options, context);
			let finished = false;
			const dispose = async (reason: BrowserRunDisposeReason) => {
				if (finished) return;
				finished = true;
				context.signal.removeEventListener("abort", onAbort);
				active.delete(dispose);
				await browserRun.dispose(reason);
			};
			const onAbort = () => browserRun.interrupt();
			active.add(dispose);
			if (context.signal.aborted) onAbort();
			else
				context.signal.addEventListener("abort", onAbort, {
					once: true,
				});
			return {
				tools: browserRun.tools(),
				dispose,
			};
		},
		close: async () => {
			await Promise.allSettled(
				[...active].map((dispose) => dispose("close")),
			);
		},
	});
}
